import { Injectable } from '@angular/core';

/**
 * Shared utility service for date/time conversions
 * Handles UTC to local time conversions and vice versa
 */
@Injectable({
  providedIn: 'root'
})
export class DateTimeHelperService {

  /**
   * Default date to send when planned date time is cleared
   * This is a very old date that indicates "no planned date" to the backend
   * Using 1900-01-01T00:00:00Z as the sentinel value
   */
  static readonly CLEARED_PLANNED_DATE = '1900-01-01T00:00:00Z';

  /**
   * Get the default date to use when planned date time is cleared
   * Returns a very old date that indicates "no planned date" to the backend
   */
  getClearedPlannedDate(): string {
    return DateTimeHelperService.CLEARED_PLANNED_DATE;
  }

  /**
   * Check if a date value represents a cleared/default planned date
   */
  isPlannedDateCleared(dateValue: string | null | undefined): boolean {
    if (!dateValue) return true;
    return dateValue === DateTimeHelperService.CLEARED_PLANNED_DATE ||
           dateValue.startsWith('1900-01-01');
  }

  /**
   * Get ordinal suffix for a day number (st, nd, rd, th)
   * Handles boundary cases: 1st, 2nd, 3rd, 21st, 22nd, 23rd, 31st, etc.
   */
  private getOrdinalSuffix(day: number): string {
    if (day >= 11 && day <= 13) {
      return 'th';
    }
    const lastDigit = day % 10;
    switch (lastDigit) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  }

  /**
   * Add ordinal suffix to a date string that contains a day number
   * Example: "Thu, 6 Nov 2025" -> "Thu, 6th Nov 2025"
   */
  addOrdinalToDateString(dateString: string): string {
    // Match day number followed by space or comma, then month name
    // Pattern: day number (1-31) followed by separator and month name (3+ letters)
    // Handles formats like "Thu, 6 Nov 2025" or "6 Nov 2025"
    // Uses replaceAll to handle multiple matches
    // ReDoS-safe: limits quantifiers to prevent catastrophic backtracking
    return dateString.replaceAll(/(\d{1,2})([\s,]{1,3})([A-Za-z]{3,20})/g, (match, day, separator, month) => {
      const dayNum = Number.parseInt(day, 10);
      // Only add ordinal if this looks like a day number (1-31)
      if (dayNum >= 1 && dayNum <= 31) {
        const suffix = this.getOrdinalSuffix(dayNum);
        return `${day}${suffix}${separator}${month}`;
      }
      return match; // Don't modify if it doesn't look like a day number
    });
  }

  /**
   * Ensure UTC timestamp has 'Z' suffix if missing
   * This ensures proper UTC parsing when timestamps come from backend without timezone info
   */
  ensureUtcTimestamp(timestamp: string | undefined | null): string {
    if (!timestamp) {
      return '';
    }
    
    // Convert to string if it's not already (handles Date objects, numbers, etc.)
    const timestampStr = String(timestamp);
    if (!timestampStr || timestampStr === 'null' || timestampStr === 'undefined') {
      return '';
    }
    
    // If timestamp already has timezone info (Z or +/-offset), return as-is
    if (timestampStr.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(timestampStr)) {
      return timestampStr;
    }
    
    // Add 'Z' to indicate UTC
    return timestampStr + 'Z';
  }

  /**
   * Convert UTC date string to local time string for datetime-local input
   * Input: UTC timestamp (with or without 'Z')
   * Output: YYYY-MM-DDTHH:mm format in local timezone
   */
  convertUtcToLocal(utcDateStr: string | undefined | null): string {
    if (!utcDateStr) return '';
    
    try {
      // Ensure UTC timestamp has 'Z' suffix if missing
      const utcTimestamp = this.ensureUtcTimestamp(utcDateStr);
      if (!utcTimestamp) return '';
      
      // Parse as UTC and convert to local time
      const utcDate = new Date(utcTimestamp);
      if (Number.isNaN(utcDate.getTime())) {
        return 'Invalid Date'; // Return Invalid Date if parsing fails
      }
      
      // Return in format suitable for datetime-local input (YYYY-MM-DDTHH:mm)
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${utcDate.getFullYear()}-${pad(utcDate.getMonth() + 1)}-${pad(utcDate.getDate())}T${pad(utcDate.getHours())}:${pad(utcDate.getMinutes())}`;
    } catch {
      return 'Invalid Date';
    }
  }

  /**
   * Convert local date string (from datetime-local input) to UTC string
   * Input: YYYY-MM-DDTHH:mm format in local timezone
   * Output: YYYY-MM-DDTHH:mm format in UTC
   */
  convertLocalToUtc(localDateStr: string): string {
    if (!localDateStr) return '';
    
    try {
      // Parse as local time (datetime-local input is always in local timezone)
      const localDate = new Date(localDateStr);
      if (Number.isNaN(localDate.getTime())) {
        return localDateStr;
      }
      
      // Convert to UTC ISO string (YYYY-MM-DDTHH:mm:ss.sssZ)
      // Then extract just YYYY-MM-DDTHH:mm format
      const utcIso = localDate.toISOString();
      return utcIso.slice(0, 16); // YYYY-MM-DDTHH:mm
    } catch {
      return localDateStr;
    }
  }

  /**
   * Format UTC date string for display (converts to local time automatically)
   * Input: UTC timestamp (with or without 'Z')
   * Output: Formatted date string in local timezone
   */
  formatDateForDisplay(utcDateStr: string | undefined | null): string {
    if (!utcDateStr) return '';
    
    try {
      // Ensure UTC timestamp has 'Z' suffix if missing
      const utcTimestamp = this.ensureUtcTimestamp(utcDateStr);
      if (!utcTimestamp) return '';
      
      // Parse as UTC - JavaScript Date automatically converts to local time when formatting
      const date = new Date(utcTimestamp);
      if (Number.isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      
      const options: Intl.DateTimeFormatOptions = {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      };
      const formatted = date.toLocaleString('en-GB', options);
      return this.addOrdinalToDateString(formatted);
    } catch {
      return 'Invalid Date';
    }
  }

  /**
   * Format UTC date string for scheduled text display
   * Input: UTC timestamp (with or without 'Z')
   * Output: Formatted string like "Thu, 6 Nov 2025, 15:30"
   */
  formatScheduledText(utcDateStr: string | undefined | null): string | undefined {
    if (!utcDateStr) return undefined;
    
    try {
      // Ensure UTC timestamp has 'Z' suffix if missing
      const utcTimestamp = this.ensureUtcTimestamp(utcDateStr);
      if (!utcTimestamp) return undefined;
      
      // Parse as UTC - JavaScript Date automatically converts to local time when formatting
      const dt = new Date(utcTimestamp);
      if (Number.isNaN(dt.getTime())) {
        return 'Invalid Date';
      }
      
      // Format date parts explicitly to ensure consistent format
      const weekday = dt.toLocaleDateString('en-GB', { weekday: 'short' });
      const day = dt.getDate();
      const month = dt.toLocaleDateString('en-GB', { month: 'short' });
      const year = dt.getFullYear();
      const dayWithOrdinal = `${day}${this.getOrdinalSuffix(day)}`;
      const datePart = `${weekday}, ${dayWithOrdinal} ${month} ${year}`;
      
      const timePart = dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
      return `${datePart}, ${timePart}`;
    } catch {
      return 'Invalid Date';
    }
  }

}




